import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CarregadoresMoneyInput } from "@/components/CarregadoresMoneyInput";
import { formatBrazilianCurrency } from "@/utils/formatters";
import {
  criarIntervaloWallboxCondominio,
  rotuloDistanciaIntervalo,
  totalIntervaloWallboxCondominio,
  type WallboxCondominioIntervalo,
} from "@/utils/wallboxCondominioPropostaUtils";

type WallboxCondominioIntervalosFieldProps = {
  intervalos: WallboxCondominioIntervalo[];
  onChange: (intervalos: WallboxCondominioIntervalo[]) => void;
};

function proximoIntervalo(intervalos: WallboxCondominioIntervalo[]): WallboxCondominioIntervalo {
  const ultimo = intervalos[intervalos.length - 1];
  if (!ultimo) return criarIntervaloWallboxCondominio(0, 20);
  const inicio = Number(ultimo.distanciaAte) || 0;
  return criarIntervaloWallboxCondominio(inicio, inicio + 20);
}

export function WallboxCondominioIntervalosField({
  intervalos,
  onChange,
}: WallboxCondominioIntervalosFieldProps) {
  const atualizar = (id: string, patch: Partial<WallboxCondominioIntervalo>) => {
    onChange(intervalos.map((intervalo) => (intervalo.id === id ? { ...intervalo, ...patch } : intervalo)));
  };

  return (
    <div className="space-y-3 rounded-lg border p-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm font-medium">Intervalos de distância</p>
          <p className="text-xs text-muted-foreground">
            Cada faixa entra na tabela da proposta com material, mão de obra e total.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onChange([...intervalos, proximoIntervalo(intervalos)])}
        >
          <Plus className="mr-2 h-4 w-4" />
          Adicionar intervalo
        </Button>
      </div>

      {intervalos.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhum intervalo. Clique em adicionar para começar.</p>
      ) : (
        <div className="space-y-3">
          {intervalos.map((intervalo, index) => {
            const total = totalIntervaloWallboxCondominio(intervalo);
            return (
              <div key={intervalo.id} className="space-y-3 rounded-md border bg-background p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium">
                    Intervalo {index + 1}
                    <span className="ml-2 font-normal text-muted-foreground">
                      ({rotuloDistanciaIntervalo(intervalo)})
                    </span>
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => onChange(intervalos.filter((item) => item.id !== intervalo.id))}
                    aria-label={`Remover intervalo ${index + 1}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor={`wbcondo-de-${intervalo.id}`}>De (m)</Label>
                    <Input
                      id={`wbcondo-de-${intervalo.id}`}
                      type="number"
                      min={0}
                      step={1}
                      value={intervalo.distanciaDe}
                      onChange={(e) => {
                        const n = parseInt(e.target.value, 10);
                        atualizar(intervalo.id, { distanciaDe: Number.isFinite(n) && n >= 0 ? n : 0 });
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`wbcondo-ate-${intervalo.id}`}>Até (m)</Label>
                    <Input
                      id={`wbcondo-ate-${intervalo.id}`}
                      type="number"
                      min={1}
                      step={1}
                      value={intervalo.distanciaAte}
                      onChange={(e) => {
                        const n = parseInt(e.target.value, 10);
                        atualizar(intervalo.id, { distanciaAte: Number.isFinite(n) && n >= 0 ? n : 0 });
                      }}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <CarregadoresMoneyInput
                    id={`wbcondo-mat-${intervalo.id}`}
                    label="Material (R$)"
                    value={Number(intervalo.precoMateriais) || 0}
                    onValueChange={(n) => atualizar(intervalo.id, { precoMateriais: n })}
                  />
                  <CarregadoresMoneyInput
                    id={`wbcondo-mo-${intervalo.id}`}
                    label="Mão de obra (R$)"
                    value={Number(intervalo.precoMaoObra) || 0}
                    onValueChange={(n) => atualizar(intervalo.id, { precoMaoObra: n })}
                  />
                </div>
                <div className="flex justify-between rounded-md bg-muted/50 px-3 py-2 text-sm">
                  <span className="text-muted-foreground">Total do intervalo</span>
                  <span className="font-semibold">{formatBrazilianCurrency(total)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
